/**
 * Scooter Pack Purchase Modal
 * Modal for fleet riders to purchase scooter packs.
 */

import React, { useEffect, useState } from 'react';
import { Popup, Toast } from 'antd-mobile';
import { ThemeProvider } from 'styled-components';
import { scooterPackService } from '../../services/api/scooterPackService';
import { walletService } from '../../services/api';
import { invalidateScooterReadiness } from '../../hooks/useScooterReadiness';
import type {
  ScooterPackTemplate,
  ScooterPackEligibility,
  ScooterPackPurchaseResponse,
} from '../../services/api/scooterPackService';
import { riderLight } from '../../theme/riderTheme';
import { formatValidityHours } from '../../utils/packValidity';
import {
  Sheet,
  SheetScroll,
  SheetSticky,
  DragHandle,
  CloseButton,
  LimeRail,
  Header,
  Eyebrow,
  EyebrowMark,
  TitleRow,
  Title,
  TitleAccent,
  RightLockup,
  Section,
  SectionLabel,
  BalanceRow,
  BalanceLabel,
  BalanceValue,
  TemplateCard,
  TemplateName,
  TemplateDesc,
  StatsRow,
  StatChip,
  PriceRow,
  PriceLabel,
  PriceValue,
  InsufficientPill,
  CTAButton,
  CTAContent,
  CTALabel,
  CTAAmount,
  CTAChevron,
  Footer,
  FooterDot,
  Notice,
  StatusContainer,
  StatusRing,
  StatusGlyph,
  StatusTitle,
  StatusBody,
  StatusKv,
  StatusButton,
  LoadingShell,
  EmptyState,
  ErrorLine,
} from '../../theme/sheetPrimitives';

interface ScooterPackPurchaseModalProps {
  visible: boolean;
  scooterVin: string;
  walletBalance: number;
  scooterOverageKm?: number;
  onClose: () => void;
  onSuccess: (result: ScooterPackPurchaseResponse) => void;
}

const packTypeLabel = (t: string): string => {
  switch (t.toUpperCase()) {
    case 'TIME_ONLY':
      return 'Time based';
    case 'DISTANCE_ONLY':
      return 'Distance based';
    case 'HYBRID':
      return 'Time + distance';
    default:
      return t;
  }
};

const formatExpiry = (expiresAt?: string): string | null => {
  if (!expiresAt) return null;
  const d = new Date(expiresAt);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
};

const showToast = (message: string) => {
  try {
    Toast.show(message);
  } catch {
    console.log(message);
  }
};

const ScooterPackPurchaseModal: React.FC<ScooterPackPurchaseModalProps> = ({
  visible,
  scooterVin,
  walletBalance,
  scooterOverageKm = 0,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [eligibility, setEligibility] = useState<ScooterPackEligibility | null>(null);
  const [selected, setSelected] = useState<ScooterPackTemplate | null>(null);
  const [result, setResult] = useState<ScooterPackPurchaseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      void loadEligibility();
    } else {
      setSelected(null);
      setResult(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, scooterVin]);

  const loadEligibility = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await scooterPackService.checkEligibility(scooterVin);
      setEligibility(res);
      if (res.available_templates.length > 0) {
        const firstAffordable = res.available_templates.find(
          t => t.price_npr <= walletBalance,
        );
        if (firstAffordable) setSelected(firstAffordable);
      }
    } catch (err: any) {
      console.error('Failed to load eligibility:', err);
      setError(err?.message || 'Failed to load eligibility');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!selected) return;
    try {
      setPurchasing(true);
      setError(null);
      const res = await scooterPackService.purchasePack({
        template_id: selected.template_id,
        scooter_vin: scooterVin,
      });
      setResult(res);
      invalidateScooterReadiness(scooterVin);
      showToast('Pack purchased');
      onSuccess(res);
    } catch (err: any) {
      console.error('Scooter pack purchase failed:', err);
      setError(err?.message || 'Purchase failed');
      showToast(err?.message || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  const renderForm = () => {
    if (!eligibility) return null;
    const activeExpiry = formatExpiry(eligibility.active_pack_expires);

    return (
      <>
        <SheetScroll>
          <Header>
            <Eyebrow>
              <EyebrowMark>S</EyebrowMark>
              Pack · Scooter
            </Eyebrow>
            <TitleRow>
              <Title>
                Extend <TitleAccent>scooter rental</TitleAccent>
              </Title>
              <RightLockup $dotColor="lime">Via wallet</RightLockup>
            </TitleRow>
          </Header>

          <Section>
            <SectionLabel>Wallet balance</SectionLabel>
            <BalanceRow>
              <BalanceLabel>Available</BalanceLabel>
              <BalanceValue>{walletService.formatCurrency(walletBalance)}</BalanceValue>
            </BalanceRow>
          </Section>

          {scooterOverageKm > 0 && (
            <Notice $tone="info">
              <strong>Rollover</strong>
              {scooterOverageKm.toFixed(1)} km of previous usage will be rolled over to the next pack(s).
            </Notice>
          )}

          {eligibility.has_active_pack && (
            <Notice $tone="info">
              <strong>Pack queue</strong>
              {activeExpiry
                ? `Your current scooter pack expires ${activeExpiry}. New packs will be queued and activate automatically.`
                : 'You already have an active scooter pack. New packs will be queued and activate automatically.'}
            </Notice>
          )}

          <Section $delay={60}>
            <SectionLabel>Available packs</SectionLabel>
            {eligibility.available_templates.length === 0 ? (
              <EmptyState>No scooter packs are available right now.</EmptyState>
            ) : (
              eligibility.available_templates.map(template => {
                const affordable = template.price_npr <= walletBalance;
                const isSelected = selected?.template_id === template.template_id;
                const validityLabel = formatValidityHours(template.validity_hours);
                return (
                  <TemplateCard
                    key={template.template_id}
                    type="button"
                    $selected={isSelected}
                    $affordable={affordable}
                    onClick={() => affordable && setSelected(template)}
                    disabled={!affordable}
                  >
                    <TemplateName>{template.template_name}</TemplateName>
                    {template.description && (
                      <TemplateDesc>{template.description}</TemplateDesc>
                    )}
                    <StatsRow>
                      <StatChip>{packTypeLabel(template.pack_type)}</StatChip>
                      {validityLabel && (
                        <StatChip>{validityLabel}</StatChip>
                      )}
                      {template.distance_limit_km != null && (
                        <StatChip>{template.distance_limit_km} km</StatChip>
                      )}
                    </StatsRow>
                    <PriceRow>
                      <PriceLabel>Price</PriceLabel>
                      <PriceValue>
                        <small>NPR</small>
                        {(template.price_npr ?? 0).toLocaleString('en-IN')}
                      </PriceValue>
                    </PriceRow>
                    {!affordable && (
                      <InsufficientPill>Insufficient balance</InsufficientPill>
                    )}
                  </TemplateCard>
                );
              })
            )}

            {error && <ErrorLine>{error}</ErrorLine>}
          </Section>
        </SheetScroll>

        <SheetSticky>
          <CTAButton
            type="button"
            $disabled={!selected || purchasing}
            disabled={!selected || purchasing}
            onClick={handlePurchase}
          >
            <CTAContent>
              <CTALabel>{purchasing ? 'Processing' : 'Purchase'}</CTALabel>
              <CTAAmount>
                {selected ? walletService.formatCurrency(selected.price_npr) : 'NPR —'}
              </CTAAmount>
            </CTAContent>
            <CTAChevron>→</CTAChevron>
          </CTAButton>
          <Footer>
            <span>
              <FooterDot $color={riderLight.accent.ok} />
              Wallet deducted
            </span>
            <span>Pack · Scooter</span>
          </Footer>
        </SheetSticky>
      </>
    );
  };

  const renderIneligible = () => (
    <SheetScroll>
      <StatusContainer>
        <StatusRing $variant="failed">
          <StatusGlyph $variant="failed">!</StatusGlyph>
        </StatusRing>
        <StatusTitle>Not eligible</StatusTitle>
        <StatusBody>
          {eligibility?.reasons?.length
            ? eligibility.reasons.join(' ')
            : 'Scooter packs are not available for this scooter at this time.'}
        </StatusBody>
        <StatusButton onClick={onClose}>Close</StatusButton>
      </StatusContainer>
    </SheetScroll>
  );

  const renderSuccess = () => (
    <SheetScroll>
      <StatusContainer>
        <StatusRing $variant="success">
          <StatusGlyph $variant="success">✓</StatusGlyph>
        </StatusRing>
        <StatusTitle>{result?.auto_activated ? 'Pack activated' : 'Pack queued'}</StatusTitle>
        <StatusBody>
          {result?.message ?? 'Your scooter pack is ready.'}
        </StatusBody>
        <StatusKv>
          New balance{' '}
          <strong>{walletService.formatCurrency(result?.remaining_wallet_balance)}</strong>
        </StatusKv>
        <StatusButton onClick={onClose}>Done</StatusButton>
      </StatusContainer>
    </SheetScroll>
  );

  return (
    <Popup
      visible={visible}
      onMaskClick={onClose}
      position="bottom"
      bodyStyle={{ background: 'transparent', padding: 0, boxShadow: 'none' }}
      destroyOnClose
    >
      <ThemeProvider theme={riderLight as never}>
        <Sheet>
          <LimeRail />
          <DragHandle />
          <CloseButton onClick={onClose}>×</CloseButton>
          {loading ? (
            <LoadingShell>
              <div />
            </LoadingShell>
          ) : result ? (
            renderSuccess()
          ) : !eligibility?.eligible ? (
            renderIneligible()
          ) : (
            renderForm()
          )}
        </Sheet>
      </ThemeProvider>
    </Popup>
  );
};

export default ScooterPackPurchaseModal;
